"use client";

import { useState, useRef, useEffect } from "react";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";

const MultiSelectWithSearch = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  className = "",
  maxHeight = 300,
  showSelectAll = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm(""); // Clear search when closing
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Toggle single option
  const handleOptionToggle = (option) => {
    const isSelected = value.some((item) => item.value === option.value);
    let newValue;

    if (isSelected) {
      newValue = value.filter((item) => item.value !== option.value);
    } else {
      newValue = [...value, option];
    }

    onChange && onChange(newValue);
  };

  // Select all filtered options
  const handleSelectAll = () => {
    const allFilteredSelected = filteredOptions.every((option) =>
      value.some((item) => item.value === option.value)
    );

    let newValue;
    if (allFilteredSelected) {
      // Deselect all filtered options
      newValue = value.filter(
        (item) => !filteredOptions.some((option) => option.value === item.value)
      );
    } else {
      // Select all filtered options
      const toAdd = filteredOptions.filter(
        (option) => !value.some((item) => item.value === option.value)
      );
      newValue = [...value, ...toAdd];
    }

    onChange && onChange(newValue);
  };

  // Check if all filtered options are selected
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) =>
      value.some((item) => item.value === option.value)
    );

  // Get display text for the trigger button
  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0].label;
    return `${value.length} items selected`;
  };

  return (
    <div className={`position-relative ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <div className="position-relative">
        <Button
          variant="outline-secondary"
          className="w-100 d-flex justify-content-between align-items-center text-start pe-5"
          style={{
            backgroundColor: "#fff",
            borderColor: "#ced4da",
            color: value.length === 0 ? "#6c757d" : "#212529",
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-truncate">{getDisplayText()}</span>
          <i className={`fa fa-chevron-${isOpen ? "up" : "down"} ms-2`} />
        </Button>

        {/* Clear button when items are selected */}
        {value.length > 0 && (
          <Button
            variant="link"
            size="sm"
            className="position-absolute"
            style={{
              right: "30px",
              top: "50%",
              transform: "translateY(-50%)",
              padding: "2px 6px",
              color: "#6c757d",
              zIndex: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onChange && onChange([]);
            }}
            title="Clear all selections"
          >
            <i className="fa fa-times" />
          </Button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-lg"
          style={{
            top: "100%",
            left: 0,
            zIndex: 1050,
            marginTop: "2px",
            maxHeight: maxHeight,
            overflowY: "auto",
          }}
        >
          {/* Search Input */}
          <div className="p-2 border-bottom">
            <InputGroup size="sm">
              <InputGroup.Text>
                <i className="fa fa-search" />
              </InputGroup.Text>
              <Form.Control
                ref={searchInputRef}
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsOpen(false);
                  }
                }}
              />
            </InputGroup>
          </div>

          {/* Select All Option */}
          {showSelectAll && filteredOptions.length > 1 && (
            <div className="px-3 py-2 border-bottom bg-light">
              <Form.Check
                type="checkbox"
                id="select-all"
                label={allFilteredSelected ? "Deselect All" : "Select All"}
                checked={allFilteredSelected}
                onChange={handleSelectAll}
                className="fw-bold"
              />
            </div>
          )}

          {/* Options List */}
          <div className="py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-muted">
                <i className="fa fa-info-circle mr-2" />
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.some(
                  (item) => item.value === option.value
                );
                return (
                  <div
                    key={option.value}
                    className="px-3 py-2 hover-bg-light"
                    onClick={() => handleOptionToggle(option)}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`option-${option.value}`}
                      label={option.label}
                      checked={isSelected}
                      onChange={() => {}} // Handled by parent div onClick
                      style={{ pointerEvents: "none" }} // Prevent double-click issues
                    />
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with selection count */}
          {/* {value.length > 0 && (
            <div className="px-3 py-2 border-top bg-light">
              <small className="text-muted">
                <i className="fa fa-check-circle me-1" />
                {value.length} item{value.length !== 1 ? "s" : ""} selected
              </small>
            </div>
          )} */}
        </div>
      )}

      {/* Custom CSS for hover effect */}
      <style jsx>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
      `}</style>
    </div>
  );
};

export default MultiSelectWithSearch;
